<template>
	<header class="dashboard-header" v-if="router.currentRoute.value.name !== 'login'">
		<div class="header-container">
			<!-- Mobile menu toggle button -->
			<button class="mobile-menu-toggle" @click="toggleMobileMenu()" aria-label="Toggle menu">
				<i class="fas" :class="mobileMenuOpen ? 'fa-times' : 'fa-bars'"></i>
			</button>

			<!-- Desktop navigation menu -->
			<nav class="desktop-nav">
				<router-link to="/" class="nav-link" :class="{ active: router.currentRoute.value.name === 'home' }">
					<i class="fas fa-home"></i>
					Accueil
				</router-link>
				<router-link v-if="user?.has_ndf" to="/expenses" class="nav-link"
							 :class="{ active: router.currentRoute.value.path.includes('/expenses') }">
					<i class="fas fa-receipt"></i>
					Notes de frais
				</router-link>
				<router-link to="/leaves" class="nav-link"
							 :class="{ active: router.currentRoute.value.path.includes('/leaves') }">
					<i class="fas fa-calendar"></i>
					Congés
				</router-link>
			</nav>

			<!-- User profile dropdown -->
			<div class="header-right">
				<n-dropdown trigger="click" :options="profileOptions" @select="handleProfileAction" placement="bottom-end">
					<n-button text>
						<template #icon>
							<div class="header-avatar me-4">
								<img v-if="userAvatar" :src="userAvatar" alt="Avatar" class="avatar-image"
									 :style="{ aspectRatio: '1' }">
								<i v-else class="fas fa-user-circle fa-lg"></i>
							</div>
						</template>
						<span class="nav-link align-middle">
							{{ userFullName }}
							<i class="fas fa-chevron-down"></i>
						</span>
					</n-button>
				</n-dropdown>
			</div>
		</div>
	</header>

	<!-- Mobile navigation overlay -->
	<div class="mobile-nav-overlay" :class="{ 'active': mobileMenuOpen }" @click="closeMobileMenu"></div>

	<!-- Mobile navigation menu -->
	<nav class="mobile-nav" :class="{ 'active': mobileMenuOpen }">
		<div class="mobile-nav-header">
			<h3 class="text-white m-0">Menu</h3>
			<button class="mobile-nav-close" @click="closeMobileMenu">
				<i class="fas fa-times"></i>
			</button>
		</div>
		<router-link to="/" class="nav-link px-3" :class="{ active: router.currentRoute.value.name === 'home' }" @click="closeMobileMenu">
			<i class="fas fa-home"></i>
			Accueil
		</router-link>
		<router-link v-if="user?.has_ndf" to="/expenses" class="nav-link px-3"
				 :class="{ active: router.currentRoute.value.path.includes('/expenses') }" @click="closeMobileMenu">
			<i class="fas fa-receipt"></i>
			Notes de frais
		</router-link>
		<router-link to="/leaves" class="nav-link px-3"
				 :class="{ active: router.currentRoute.value.path.includes('/leaves') }" @click="closeMobileMenu">
			<i class="fas fa-calendar"></i>
			Congés
		</router-link>
	</nav>
	<div class="container-fluid d-flex flex-column" style="overflow-x: hidden; width: 100%;">
		<main class="bd-main flex-grow-1" style="padding-bottom: 60px;">
			<div class="row justify-content-center">
				<div class="col-12">
					<div>
						<n-config-provider :locale="frFR" :date-locale="dateFrFR" :theme-overrides="themeOverrides">
							<n-message-provider>
								<router-view></router-view>
							</n-message-provider>
						</n-config-provider>
					</div>
				</div>
			</div>
		</main>
	</div>
	<!-- Footer -->
	<footer class="app-footer mt-auto" v-if="router.currentRoute.value.name !== 'login'">
		<div class="footer-container">
			<div class="footer-left">
				© 2025 Tous droits réservés
			</div>
			<div class="footer-right">
				Créé avec <i class="fa fa-heart text-danger"></i> par
				<a href="https://conselio.com" target="_blank" class="font-weight-medium">conselio</a>
				- version : {{ version }}
			</div>
		</div>
	</footer>
</template>

<script setup>
import {useStore} from "vuex";
import {frFR, dateFrFR} from 'naive-ui'
import {version} from './version.js';
import {computed, h, ref} from 'vue';
import router from "./routeur.js";

const store = useStore();

const user = computed(() => store.getters.getUser);
const userFullName = computed(() => store.getters.getDisplayName);
const mobileMenuOpen = ref(false);

// Boutons du système de design un peu trop petits (ex: "Maintenant"/"Confirmer"
// dans le date-picker), on augmente les tailles communes pour un meilleur touch target mobile
const themeOverrides = {
	common: {
		heightTiny: '30px',
		heightSmall: '32px',
		heightMedium: '38px',
		heightLarge: '44px',
		fontSizeTiny: '13px',
		fontSizeSmall: '14px',
		fontSizeMedium: '15px',
		fontSizeLarge: '16px',
	},
	DatePicker: {
		itemSize: '32px',
		itemCellWidth: '44px',
		itemCellHeight: '38px',
		itemFontSize: '15px',
		scrollItemWidth: '90px',
		scrollItemHeight: '46px',
		panelActionPadding: '10px 14px',
	},
};

const userAvatar = computed(() => {
	const user = store.getters.getUser;
	if (!user?.avatar) return null;
	const pb = store.getters.getPb;
	return pb.files.getUrl(user, user.avatar, {'thumb': '32x32'});
});

function closeMobileMenu() {
	mobileMenuOpen.value = false;
	document.body.classList.remove('menu-open');
}

function toggleMobileMenu() {
	mobileMenuOpen.value = !mobileMenuOpen.value;
	if (mobileMenuOpen.value) {
		document.body.classList.add('menu-open');
	} else {
		document.body.classList.remove('menu-open');
	}
}

const profileOptions = [
	{
		label: 'Mon profil',
		key: 'profile',
		icon: renderIcon('fas fa-user')
	},
	{
		type: 'divider',
		key: 'd1'
	},
	{
		label: 'Déconnexion',
		key: 'logout',
		icon: renderIcon('fas fa-sign-out-alt')
	}
];

function renderIcon(icon) {
	return () => h('i', {class: icon});
}

async function handleProfileAction(key) {
	switch (key) {
		case 'profile':
			router.push('/profile');
			break;
		case 'logout':
			await logout();
			break;
	}
}

async function logout() {
	await store.dispatch('logout');
	window.location.reload();
}
</script>

<style scoped>
/* Footer styles */
.app-footer {
	background-color: #f8f9fa;
	padding: 1rem 0;
	border-top: 1px solid #e9ecef;
	width: 100%;
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 50;
}

.footer-container {
	max-width: 1400px;
	margin: 0 auto;
	padding: 0 1rem;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 1rem;
}

.footer-left, .footer-right {
	display: flex;
	align-items: center;
}

.footer-right {
	gap: 0.25rem;
}

.text-danger {
	color: #dc3545;
}

.font-weight-medium {
	font-weight: 500;
	text-decoration: none;
	color: #0d6efd;
}

.font-weight-medium:hover {
	text-decoration: underline;
}

@media (max-width: 768px) {
	.footer-container {
		flex-direction: column;
		text-align: center;
		gap: 0.5rem;
	}
}

header {
	background-color: #343a40;
	position: sticky;
	top: 0;
	z-index: 100;
}

.header-container {
	max-width: 1400px;
	margin: 0 auto;
	padding: 0.75rem 1rem;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

/* Mobile menu toggle button - hidden on desktop */
.mobile-menu-toggle {
	display: none;
	background: transparent;
	border: none;
	color: rgba(255, 255, 255, 0.8);
	font-size: 1.25rem;
	cursor: pointer;
	padding: 0.5rem;
	transition: color 0.2s;
	z-index: 101;
}

.mobile-menu-toggle:hover {
	color: #ffffff;
}

/* Desktop navigation */
.desktop-nav {
	display: flex;
	gap: 1.5rem;
}

.nav-link {
	text-decoration: none;
	color: rgba(255, 255, 255, 0.8);
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem;
	border-radius: 0.375rem;
	transition: all 0.2s;
}

.nav-link:hover {
	color: #ffffff;
	background-color: rgba(255, 255, 255, 0.1);
}

.nav-link.active {
	color: #ffffff;
	background-color: rgba(255, 255, 255, 0.15);
}

.nav-link i {
	font-size: 1rem;
}

.header-right {
	display: flex;
	align-items: center;
	gap: 1rem;
}
:deep(.n-dropdown-menu) {
	min-width: 200px;
}

.header-avatar {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 32px;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	overflow: hidden;
	background-color: rgba(255, 255, 255, 0.1);
	aspect-ratio: 1;
	margin-right: 0 !important;
}

.header-avatar .avatar-image {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

/* Mobile navigation overlay */
.mobile-nav-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	z-index: 90;
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.3s ease, visibility 0.3s ease;
}

.mobile-nav-overlay.active {
	opacity: 1;
	visibility: visible;
}

/* Mobile navigation menu */
.mobile-nav {
	position: fixed;
	top: 0;
	left: 0;
	width: 80%;
	max-width: 300px;
	height: 100%;
	background-color: #343a40;
	z-index: 95;
	overflow-y: auto;
	padding: 1rem;
	transform: translateX(-100%);
	transition: transform 0.3s ease;
	visibility: hidden;
}

.mobile-nav.active {
	visibility: visible;
	transform: translateX(0);
}

.mobile-nav-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1.5rem;
	padding-bottom: 0.5rem;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-nav-close {
	background: transparent;
	border: none;
	color: rgba(255, 255, 255, 0.8);
	font-size: 1.25rem;
	cursor: pointer;
}

.mobile-nav .nav-link {
	padding: 0.75rem 0;
	width: 100%;
}

/* Ajustement du main content pour le header fixe */
.bd-main {
	padding-top: 1rem;
}

/* Responsive styles */
@media (max-width: 768px) {
	.header-container {
		position: relative;
	}

	.mobile-menu-toggle {
		display: block;
		position: relative;
	}

	.desktop-nav {
		display: none;
	}

	:deep(.n-dropdown-menu) {
		position: fixed !important;
	}

	/* Ensure the mobile menu doesn't overflow the viewport */
	.mobile-nav {
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
	}

	/* Prevent scrolling of the body when menu is open */
	body.menu-open {
		overflow: hidden;
	}
}
</style>
